import React from "react";
import { useTranslation } from "react-i18next";
import { OrderGroupDetail } from "@/api/models/api-types";

interface OrderPaymentSectionProps {
  orderGroupDetail?: OrderGroupDetail;
}

export const OrderPaymentSection: React.FC<OrderPaymentSectionProps> = ({
  orderGroupDetail,
}) => {
  const { t } = useTranslation();

  // Get payment info from orderGroupDetail only (Order type doesn't have payment properties)
  const paymentMethod = orderGroupDetail?.paymentMethod;
  const cardAssociation = orderGroupDetail?.cardAssociation;
  const lastDigits = orderGroupDetail?.lastDigits;

  if (!paymentMethod) return null;

  // Format payment method display
  const getPaymentMethodDisplay = () => {
    if (cardAssociation && lastDigits) {
      return `${cardAssociation} •••• ${lastDigits}`;
    }
    if (!paymentMethod) return t("orders.payment");

    const normalizedMethod = paymentMethod.toLowerCase();
    const paymentMethodKey = `orders.paymentMethods.${normalizedMethod}`;
    const translatedMethod = t(paymentMethodKey);

    if (translatedMethod !== paymentMethodKey) {
      return translatedMethod;
    }

    return paymentMethod
      .replace(/_/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  // Get card icon (simplified - can be enhanced with actual card icons)
  const getCardIcon = () => {
    if (cardAssociation === "VISA") {
      return "💳";
    }
    if (cardAssociation === "MASTERCARD") {
      return "💳";
    }
    return "💳";
  };

  return (
    <div className="flex items-start justify-between px-4 md:px-6 py-4 md:py-6">
      <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary">
        {t("orders.payment")}
      </h3>
      <div className="flex items-center gap-2 md:gap-3">
        {/* Card Icon */}
        <div className="w-12 h-8 md:w-14 md:h-10 bg-white dark:bg-gray-800 border border-neutral-100 dark:border-gray-700 rounded flex items-center justify-center flex-shrink-0">
          <span className="text-base md:text-lg">{getCardIcon()}</span>
        </div>

        {/* Payment Details */}
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-text-primary dark:text-text-primary">
            {getPaymentMethodDisplay()}
          </p>
          {cardAssociation && (
            <p className="text-xs text-text-tertiary dark:text-text-tertiary">
              {t("orders.expires")}{" "}
              {orderGroupDetail?.metadata?.expiryDate || "10/2028"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
